import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { PaymentGridOverriddenInputs } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { FieldOverrideKey } from '../../../hooks/useFieldOverrideIndicator';
import { useFieldOverrideIndicator, useFieldOverrideMessage } from '../../../hooks/useFieldOverrideIndicator';
import { useIsAspPaymentGridRedesignActive } from './useIsAspPaymentGridRedesignActive';

export type PaymentGridOverrideField = keyof PaymentGridOverriddenInputs;

export type PaymentGridOverrideIndicatorState = {
    enabled: boolean;
    isShown: (index: number) => boolean;
    message: string;
    recordManualOverride: () => void;
};

export const DEAL_SCREEN_OVERRIDE_FIELD: Record<PaymentGridOverrideField, FieldOverrideKey> = {
    sellRate: 'sellRate',
    residual: 'baseResidual'
};

export const usePaymentGridOverrideIndicator = (field: PaymentGridOverrideField): PaymentGridOverrideIndicatorState => {
    const isToggleEnabled = useSelector(featureToggleSelectors.isPgOverrideIndicatorEnabled);
    const isRedesignActive = useIsAspPaymentGridRedesignActive();
    const enabled = isToggleEnabled && isRedesignActive;
    const activeColumnIndex = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex);
    const { show: isDealScreenOverridden, recordManualOverride } = useFieldOverrideIndicator(DEAL_SCREEN_OVERRIDE_FIELD[field]);
    const gridOverridden = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridOverriddenInputs(state)[field]);

    const isShown = useCallback(
        (index: number) => enabled && (index === activeColumnIndex ? isDealScreenOverridden : Boolean(gridOverridden[index])),
        [enabled, activeColumnIndex, isDealScreenOverridden, gridOverridden]
    );

    const message = useFieldOverrideMessage(DEAL_SCREEN_OVERRIDE_FIELD[field]);

    return { enabled, isShown, message, recordManualOverride };
};
